import { airHandlerTypeCodes } from 'data/constants';
import { AirHandlerTypeCodeType, LevelType, SpaceType } from 'data/types';
import { getItem } from 'utils/data';
import {
  getProjectSettingValue,
  isDuctedOnlyPreference,
  isDuctedOrHybridPreference
} from 'utils/project';
import { isDuctedOnlyAirHandlerCode } from 'utils/resolve-zone-type';

export const AIR_HANDLER_UNAVAILABLE_REASON =
  'No matching systems include this air handler type for this configuration.';

type RuleContext = {
  attrs: Record<string, string | number | undefined>;
  item: ReturnType<typeof getItem>;
  spaceLabel: string;
  sectionSpaceCount: number;
};

type GlobalRule = {
  codes: AirHandlerTypeCodeType[];
  reason: string;
  applies: () => boolean;
};

type SpaceRule = {
  codes: (ctx: RuleContext) => AirHandlerTypeCodeType[];
  reason: (ctx: RuleContext) => string;
  applies: (ctx: RuleContext) => boolean;
};

const MINI_SPLIT_HEAD_CODES = Object.values(airHandlerTypeCodes).filter(
  (code) => !isDuctedOnlyAirHandlerCode(code)
);

const GLOBAL_RULES: GlobalRule[] = [
  {
    codes: ['evap_coil'],
    reason: 'Not offered for ductless mini-split zones.',
    applies: () => !isDuctedOnlyPreference()
  },
  {
    // AHA-6784: on the hybrid and ducted paths, 'ducted' droppability is
    // governed solely by system data (perZoneTypes); everywhere else it
    // stays globally restricted as before.
    codes: ['ducted'],
    reason: 'Not offered for ductless mini-split zones.',
    applies: () => !isDuctedOrHybridPreference()
  },
  {
    codes: MINI_SPLIT_HEAD_CODES,
    reason: 'Not offered for ducted systems.',
    applies: () => isDuctedOnlyPreference()
  },
  {
    codes: ['cassette'],
    reason:
      'Joist width is 16 inches; only the slim-fit ceiling cassette fits between the joists.',
    applies: () => getProjectSettingValue('joist_width') === '16"'
  }
];

const SPACE_RULES: SpaceRule[] = [
  {
    codes: (ctx) => {
      if (!ctx.item || ctx.item.allowedAirHandlerTypes.length === 0) return [];
      const allCodes = Object.values(airHandlerTypeCodes);
      return allCodes.filter(
        (type) =>
          !ctx.item!.allowedAirHandlerTypes.includes(type) &&
          // AHA-6784: space item data predates 'ducted' — on the hybrid and
          // ducted paths a central AH has no per-space allow-list restriction.
          !(isDuctedOnlyAirHandlerCode(type) && isDuctedOrHybridPreference())
      );
    },
    reason: (ctx) => `Not supported for the '${ctx.spaceLabel}' space type.`,
    applies: (ctx) => !!ctx.item && ctx.item.allowedAirHandlerTypes.length > 0
  },
  {
    codes: () => ['cassette', 'cassette_16'],
    reason: () =>
      'Ceiling cassettes need a flat ceiling with accessible space above.',
    applies: (ctx) =>
      ctx.attrs.ceiling_type === 'Vaulted' ||
      ctx.attrs.ceiling_type === 'Sloped' ||
      ctx.attrs.space_above !== 'Yes'
  },
  {
    codes: () => ['cassette_drop_ceiling'],
    reason: () => 'Requires a drop ceiling.',
    applies: (ctx) => ctx.attrs.ceiling_type !== 'Drop Ceiling'
  },
  {
    codes: () => ['wall', 'wallDIY', 'heatingConsole'],
    reason: () => 'Ceiling height is under 6 ft.',
    applies: (ctx) => Number(ctx.attrs.ceiling_height) < 6
  },
  {
    codes: () => ['concealed'],
    reason: () =>
      'Concealed-duct units need space above or below for the ductwork.',
    applies: (ctx) =>
      ctx.attrs.space_above !== 'Yes' && ctx.attrs.space_below !== 'Yes'
  },
  {
    codes: () => [
      'wall',
      'cassette',
      'heatingConsole',
      'console',
      'wallDIY',
      'cassette_16',
      'cassette_drop_ceiling'
    ],
    reason: () =>
      "Spaces in this zone don't share air, so one air handler can't condition them all.",
    applies: (ctx) => ctx.attrs.share_air === 'No' && ctx.sectionSpaceCount > 1
  }
];

function findSpace(
  structure: LevelType[],
  spaceId: string
): SpaceType | undefined {
  for (const level of structure) {
    for (const section of level.sections) {
      const match = section.spaces.find((sp) => sp.uniqueId === spaceId);
      if (match) return match;
    }
  }
  return undefined;
}

function buildContext(
  space: SpaceType,
  sectionSpaceCount: number
): RuleContext {
  const attrs = Object.fromEntries(
    space.attributes.map((a) => [a.code, a.value])
  ) as Record<string, string | number | undefined>;

  return {
    attrs,
    item: getItem(space.itemId),
    spaceLabel: space.label,
    sectionSpaceCount
  };
}

function collectRestrictedTypes(
  spaceIds: string[],
  structure: LevelType[]
): AirHandlerTypeCodeType[] {
  const restricted = new Set<AirHandlerTypeCodeType>();

  GLOBAL_RULES.forEach((rule) => {
    if (rule.applies()) {
      rule.codes.forEach((code) => restricted.add(code));
    }
  });

  spaceIds.forEach((spaceId) => {
    const space = findSpace(structure, spaceId);
    if (!space) return;

    const ctx = buildContext(space, spaceIds.length);
    SPACE_RULES.forEach((rule) => {
      if (rule.applies(ctx)) {
        rule.codes(ctx).forEach((code) => restricted.add(code));
      }
    });
  });

  return Array.from(restricted);
}

export function getRestrictedAirHandlerTypesForSection(
  sectionSpaceIds: string[],
  structure: LevelType[]
): AirHandlerTypeCodeType[] {
  return collectRestrictedTypes(sectionSpaceIds, structure);
}

export function getRestrictedAirHandlerTypesForSpace(
  spaceId: string,
  structure: LevelType[]
): AirHandlerTypeCodeType[] {
  return collectRestrictedTypes([spaceId], structure);
}

export function getSectionRestrictionReasons(
  code: AirHandlerTypeCodeType,
  sectionSpaceIds: string[],
  structure: LevelType[]
): string[] {
  const reasons = new Set<string>();

  GLOBAL_RULES.forEach((rule) => {
    if (rule.applies() && rule.codes.includes(code)) {
      reasons.add(rule.reason);
    }
  });

  sectionSpaceIds.forEach((spaceId) => {
    const space = findSpace(structure, spaceId);
    if (!space) return;

    const ctx = buildContext(space, sectionSpaceIds.length);
    SPACE_RULES.forEach((rule) => {
      if (rule.applies(ctx) && rule.codes(ctx).includes(code)) {
        reasons.add(rule.reason(ctx));
      }
    });
  });

  return Array.from(reasons);
}

export function getLevelRestrictionReasons(
  code: AirHandlerTypeCodeType,
  level: LevelType,
  structure: LevelType[]
): string[] {
  const reasons = new Set<string>();

  level.sections.forEach((section) => {
    const sectionSpaceIds = section.spaces.map((sp) => sp.uniqueId);
    getSectionRestrictionReasons(code, sectionSpaceIds, structure).forEach(
      (reason) => reasons.add(reason)
    );
  });

  return Array.from(reasons);
}
